import { createRoutine, promisifyRoutine } from 'redux-saga-routines';
import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import Auth from '@aws-amplify/auth';
import { get, mapKeys, camelCase } from 'lodash/fp';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module 'quer... Remove this comment to see the full error message
import queryString from 'query-string';
import * as FullStory from '@fullstory/browser';
import { AnyAction } from 'redux';
import { IMPERSONATOR_TOKEN } from '@whitelabel/helpers/constants';
import api, { API_HOST, handleFormikAWSFormError } from '../helpers/api';
import getCookie from '../helpers/cookie';
import { IMPERSONATE, SOURCE_EMAIL, SOURCE_SMS } from '../helpers/constants';
import {
  handleGetCustomer as getCustomer,
  handleUpdateCustomer as updateCustomer,
  clearCustomer,
  handleTransferBookings as transferBookings,
} from './customer';
import { clearBookings } from './bookings';
import { clearClaims } from './claims';
import { clearCOI } from './coi';
import { clearPDS } from './pds';
import { clearPartners } from './partners';

export const checkSession = createRoutine('user/CHECK_SESSION');
export const login = createRoutine('user/LOGIN');
export const impersonateLogin = createRoutine('user/IMPERSONATE_LOGIN');
export const logout = createRoutine('user/LOGOUT');
export const confirmSignUp = createRoutine('user/CONFIRM_SIGNUP');
export const resendSignUp = createRoutine('user/RESEND_SIGNUP');
export const forgotPassword = createRoutine('user/FORGOT_PASSWORD');
export const forgotPasswordSubmit = createRoutine('user/FORGOT_PASSWORD_SUBMIT');
export const changePassword = createRoutine('user/CHANGE_PASSWORD');
export const resetError = createRoutine('user/RESET_USER_ERROR');

export const forgotPasswordPromiseRoutine = promisifyRoutine(forgotPassword);

const getUserAttributes = (cognitoUser: any) =>
  mapKeys(camelCase, {
    username: cognitoUser.username,
    ...cognitoUser.attributes,
  });

const clearImpersonateValues = () => {
  sessionStorage.removeItem(IMPERSONATE.CUSTOMER_ID);
  sessionStorage.removeItem(IMPERSONATE.CUSTOMER_EMAIL);
  sessionStorage.removeItem(IMPERSONATOR_TOKEN);
  sessionStorage.removeItem(IMPERSONATE.ADMIN_USERNAME);
};

const tagFullStory = (username: string) => {
  const adminUsername = sessionStorage.getItem(IMPERSONATE.ADMIN_USERNAME);
  const customerEmail = sessionStorage.getItem(IMPERSONATE.CUSTOMER_EMAIL);
  FullStory.identify(username, {
    displayName: `Admin: ${adminUsername} as ${customerEmail}`,
  });
};

function identifyUser(
  username: string,
  { id, firstName, lastName, email }: { id: string; firstName: string; lastName: string; email: string },
) {
  if (!window.isHeadless) {
    window.bwtag('identify', username);
    FullStory.identify(id, {
      displayName: `${firstName} ${lastName}`,
      email,
    });
  }
}

function* handleCheckSession() {
  try {
    const id = sessionStorage.getItem(IMPERSONATE.CUSTOMER_ID);
    const email = sessionStorage.getItem(IMPERSONATE.CUSTOMER_EMAIL);
    // check session for 'login as customer' flow
    if (id) {
      yield put(checkSession.request());
      const user = {
        username: id,
        email,
      };
      yield call<any>(getCustomer, { payload: user.username });
      yield put(checkSession.success(user));
    } else {
      // usual check session flow
      yield put(checkSession.request());
      window.Auth = Auth;
      const cognitoUser = yield call([Auth, Auth.currentAuthenticatedUser]);
      const user = getUserAttributes(cognitoUser);
      yield call<any>(getCustomer, { payload: user.username });
      yield put(checkSession.success(user));
    }
  } catch (error) {
    yield put(checkSession.failure());
  } finally {
    yield put(checkSession.fulfill());
  }
}

export function* handleImpersonateLogin({ payload: { username, email } }: any) {
  try {
    yield put(impersonateLogin.request());
    const user = {
      username,
      email,
    };
    sessionStorage.setItem(IMPERSONATE.CUSTOMER_EMAIL, email);
    if (!window.isHeadless) {
      tagFullStory(username);
    }
    yield put(impersonateLogin.success(user));
  } catch (error) {
    yield put(impersonateLogin.failure(handleFormikAWSFormError(error)));
  } finally {
    yield put(impersonateLogin.fulfill());
  }
}

function* handleLogin({
  payload: { cognitoUserID, password, setSubmitting, shouldRedirect = true, transferBookingsData = null },
}: any) {
  try {
    sessionStorage.removeItem(IMPERSONATOR_TOKEN);
    const locale = yield select(get('intl.locale'));
    const redirect = yield select(get('router.location.state.referrer'));

    yield put(login.request());
    const cognitoUser = yield call([Auth, Auth.signIn], cognitoUserID, password);
    const user = getUserAttributes(cognitoUser);

    yield call<any>(getCustomer, { payload: user.username });
    const customer = yield select(get('customer.data'));

    identifyUser(user.username, customer);

    if (transferBookingsData) {
      yield call(transferBookings, {
        payload: {
          targetCustomerID: customer.id,
          customerID: transferBookingsData.customerID,
          securityToken: transferBookingsData.securityToken,
        },
      });
    }
    const gdprConsent = !!getCookie('CookieConsent');

    if (gdprConsent && !customer.gdprConsent) {
      yield call(updateCustomer, {
        payload: {
          ...customer,
          gdprConsent,
        },
      });
    }
    yield put(login.success(user));

    if (shouldRedirect) {
      yield put(push(redirect || `/${locale}/account`));
    } else {
      setSubmitting(false);
    }
  } catch (error) {
    yield put(login.failure(handleFormikAWSFormError(error)));
    setSubmitting(false);
  } finally {
    yield put(login.fulfill());
  }
}

function* handleLogout({ payload: redirect }: AnyAction) {
  try {
    yield put(logout.request());
    yield call([Auth, Auth.signOut]);
    if (!window.isHeadless) window.bwtag('reset');
    clearImpersonateValues();
    yield put(clearCustomer());
    yield put(clearBookings());
    yield put(clearCOI());
    yield put(clearPDS());
    yield put(clearPartners());
    yield put(clearClaims());
    yield put(logout.success());

    if (redirect) {
      yield put(push(redirect));
    }
  } catch (error) {
    yield put(logout.failure(error));
  } finally {
    yield put(logout.fulfill());
  }
}

function* handleConfirmSignUp({ payload: { id, email, password, phone, setSubmitting, source, setErrors } }: any) {
  try {
    const locale = yield select(get('intl.locale'));
    const urlParams = new URLSearchParams(yield select(get('router.location.search')));
    const token = urlParams.get('token');
    const securityToken = urlParams.get('securityToken');
    const fromID = urlParams.get('from_id');
    const fromToken = urlParams.get('from_token');
    const redirect = yield select(get('router.location.state.referrer'));
    const { search, pathname } = redirect || {};
    const stateSearch = new URLSearchParams(search);
    stateSearch.delete('signup_token');
    stateSearch.delete('id');
    const redirectState = { pathname, search: stateSearch.toString() ? `?${stateSearch.toString()}` : '' };

    const searchParams = new URLSearchParams({ language: locale, security_token: token || securityToken! });
    const body: {
      source: typeof SOURCE_EMAIL | typeof SOURCE_SMS;
      password: string;
      email?: string;
      phone?: string;
    } = {
      source,
      password,
    };
    if (email) {
      body.email = email;
    }
    if (phone) {
      body.phone = phone;
    }
    yield put(confirmSignUp.request());
    yield call(api.post, `${API_HOST}/customers/${id}/account/?${searchParams}`, false, {
      body: JSON.stringify(body),
    });
    const cognitoUser = yield call([Auth, Auth.signIn], id, password);
    const user = getUserAttributes(cognitoUser);

    if (fromID && fromToken) {
      yield call(transferBookings, {
        payload: {
          targetCustomerID: id,
          customerID: fromID,
          securityToken: fromToken,
        },
      });
    }

    yield call<any>(getCustomer, { payload: user.username });
    const customer = yield select(get('customer.data'));

    identifyUser(user.username, customer);
    yield put(confirmSignUp.success(user));
    yield put(redirect ? push(redirectState) : push(`/${locale}/account`));
  } catch (error) {
    const errors = (error as { errors: { [key: string]: string[] } })?.errors;
    if (errors && !errors._error) {
      setErrors(errors);
    } else {
      setErrors({ showCommonError: true });
    }
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    setSubmitting(false);
  } finally {
    yield put(confirmSignUp.fulfill());
  }
}

function* handleResendSignUp({ payload: email }: AnyAction) {
  try {
    yield put(resendSignUp.request());
    yield call([Auth, Auth.resendSignUp], email);
    yield put(resendSignUp.success());
  } catch (error) {
    yield put(resendSignUp.failure(error));
  } finally {
    yield put(resendSignUp.fulfill());
  }
}

function* handleForgotPassword({ payload: { isPhone, value } }: any) {
  try {
    const locale = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale });
    const body = JSON.stringify({ [isPhone ? 'phone' : 'email']: value });
    yield put(forgotPassword.request());
    yield call(api.post, `${API_HOST}/customers/forgot_password_initiate/?${searchParams}`, false, { body });
    yield put(forgotPassword.success());
  } catch (error) {
    yield put(forgotPassword.failure(handleFormikAWSFormError(error)));
  } finally {
    yield put(forgotPassword.fulfill());
  }
}

function* handleForgotPasswordSubmit({ payload: { id, password, passwordToken, setSubmitting } }: any) {
  try {
    const locale = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale });
    const body = JSON.stringify({ token: passwordToken, password });
    yield put(forgotPasswordSubmit.request());
    yield call(api.post, `${API_HOST}/customers/${id}/forgot_password_confirm/?${searchParams}`, false, { body });
    yield call(handleLogin, { payload: { cognitoUserID: id, password, setSubmitting } });
    yield put(forgotPasswordSubmit.success());
  } catch (error) {
    yield put(forgotPasswordSubmit.failure(handleFormikAWSFormError(error)));
  } finally {
    setSubmitting(false);
    yield put(forgotPasswordSubmit.fulfill());
  }
}

export function* userSaga() {
  yield takeLatest(checkSession.TRIGGER, handleCheckSession);
  yield takeLatest(login.TRIGGER, handleLogin);
  yield takeLatest(impersonateLogin.TRIGGER, handleImpersonateLogin);
  yield takeLatest(logout.TRIGGER, handleLogout);
  yield takeLatest(confirmSignUp.TRIGGER, handleConfirmSignUp);
  yield takeLatest(resendSignUp.TRIGGER, handleResendSignUp);
  yield takeLatest(forgotPassword.TRIGGER, handleForgotPassword);
  yield takeLatest(forgotPasswordSubmit.TRIGGER, handleForgotPasswordSubmit);
}
